import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { BillingAccess, BillingPlan, OrgBillingMode, UserFeatures } from '@chatsdet/types';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';
import { accountDeactivatedDuringRefresh } from '../lib/auth-refresh';
import { switchOrganizationRequest, setDefaultOrganizationRequest } from './authApi';
import { useAuthConfig } from '../hooks/useAuthConfig';
import { useToast } from '@chatsdet/ui';
import {
  ORG_CHANNEL_NAME,
  consumeOrganizationChangedNotice,
  getRenderedOrganizationId,
  movedHostUrl,
  reloadForOrganizationChange,
  resolveOrgDeepLink,
  setRenderedOrganizationId,
} from '../lib/organization-context';

export type User = {
  id: string;
  email: string | null;
  name: string | null;
  picture?: string | null;
  role: string;
  /** Empty string means the user is authenticated but has no organization. */
  orgId: string;
  isSuperadmin?: boolean;
  /** True for superadmins, or for anyone when the `plg.self_serve_org_creation` knob is on. */
  canCreateOrganization: boolean;
  /** True for superadmins, or for anyone when the knob is on: shows the Billing & usage page. */
  canViewBilling: boolean;
  /** True when the knob is on: the trial banner may show self-serve states. */
  canUseSelfServeBilling: boolean;
  isInternalOrg?: boolean;
  mfaEnrolled?: boolean;
  mfaRequired?: boolean;
  features?: UserFeatures;
  ephemeralEmailDomain?: string;
  // Organizations included from /me response
  organizations?: Organization[];
};

export type Organization = {
  id: string;
  name: string;
  role: 'admin' | 'member';
  trialEndsAt: string | null;
  billingMode: OrgBillingMode;
  /** Null for a contract org. */
  plan: BillingPlan | null;
  access: BillingAccess;
  /** Opt-in vanity app subdomain (`{appSubdomain}.app.<domain>`); null on the shared app host. */
  appSubdomain: string | null;
  /** True when this is the calling user's default org — where they land after signing in on the shared app host. */
  isDefault: boolean;
};

export type CreateOrgInput = { name: string; first?: boolean; redirectUrl?: string };

type AuthContextType = {
  user: User | null;
  organizations: Organization[] | undefined;
  isLoading: boolean;
  isAccountDeactivated: boolean;
  /** True when this host is a per-org tenant host whose label matches no organization. */
  isWorkspaceNotFound: boolean;
  /**
   * The org this hostname belongs to, when it is a per-org host — known before
   * anyone signs in, which is what lets the login screen name it. Null on the
   * shared host, i.e. everywhere today.
   */
  hostOrganizationName: string | null;
  switchOrg: (orgId: string, redirectUrl?: string) => void;
  /** `first` is set by the signup naming screen; `redirectUrl` is where to land after switching in. */
  createOrg: (input: CreateOrgInput) => void;
  setDefaultOrg: (orgId: string) => void;
  signOut: () => void;
  refetchUser: () => Promise<void>;
  isSwitchingOrg: boolean;
  isCreatingOrg: boolean;
  isSettingDefaultOrg: boolean;
};

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

/**
 * Move to the host this org answers on now, preserving the path so an old
 * bookmark lands where it meant to. A full navigation, not a router push: it is
 * a different origin with its own cookie jar.
 *
 * Returns false when the current host has no per-org shape to swap a label
 * into — `movedHostUrl` fails closed rather than guessing an origin — so the
 * caller can fall through instead of assuming it navigated. Shared by the two
 * ways we learn a label was retired (`/me`'s 409 when signed in,
 * `/auth/config` when not) so they cannot redirect differently.
 */
function redirectToMovedHost(appSubdomain: string): boolean {
  const moved = movedHostUrl(window.location, appSubdomain);
  if (!moved) return false;
  window.location.replace(moved);
  return true;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [isAccountDeactivated, setIsAccountDeactivated] = React.useState(false);
  // Never reset, like `isAccountDeactivated` beside it: both render a terminal
  // full-page screen with nowhere to navigate from, and the condition they
  // describe (this hostname belongs to no org) cannot resolve itself without a
  // reload.
  const [isWorkspaceNotFoundFromMe, setIsWorkspaceNotFoundFromMe] = React.useState(false);

  // `/me` answers the same two host questions — a 404 for an unclaimed label, a
  // 409 for a retired one — but only for a caller who HAS a session. It returns
  // 401 before it ever resolves the host, so a signed-out visitor on a stale
  // bookmark reaches neither. This public endpoint is the pre-auth half of the
  // same answer, and both halves are handled here so there is one place that
  // decides what a hostname means to the client.
  const { data: authConfig, isPending: hostPending } = useAuthConfig();
  const loginHost = authConfig?.host;

  // A `moved` label we could not build a URL for. `movedHostUrl` fails closed
  // rather than guessing an origin, and when it does there is nothing to render
  // here: the server has already declared this host retired, so every request
  // from it is refused. Falling through would park the user on a sign-in form
  // that cannot work — the same dead end the not-found screen exists to
  // replace — so treat it as one.
  const [movedRedirectFailed, setMovedRedirectFailed] = React.useState(false);

  const isWorkspaceNotFound =
    isWorkspaceNotFoundFromMe || loginHost?.kind === 'unknown' || movedRedirectFailed;

  // The pre-auth half of the 409 redirect in `fetchMe` below. Narrowed to the
  // label rather than the whole query result so a background refetch that
  // changes nothing does not re-run this.
  const movedToLabel = loginHost?.kind === 'moved' ? loginHost.appSubdomain : null;
  React.useEffect(() => {
    if (movedToLabel && !redirectToMovedHost(movedToLabel)) {
      setMovedRedirectFailed(true);
    }
  }, [movedToLabel]);

  // The global fetch interceptor handles 401 → refresh → retry transparently,
  // so this function only needs a single attempt. If /me still returns 401
  // after the interceptor's retry, the user is unauthenticated.
  const fetchMe = React.useCallback(async (): Promise<User | null> => {
    const res = await fetch(API_URL + '/me', { credentials: 'include' });
    if (res.status === 401) {
      // Check if the refresh attempt revealed account deactivation
      if (accountDeactivatedDuringRefresh) {
        setIsAccountDeactivated(true);
      }
      return null;
    }
    if (res.status === 404) {
      // A per-org tenant host (<label>.api.<domain>) whose label matches no
      // organization — the middleware refuses every request on that host,
      // /me included. Distinct from a normal 404 elsewhere in the app.
      const body = await res.json().catch(() => ({}));
      if (body?.code === 'ORGANIZATION_NOT_FOUND') {
        setIsWorkspaceNotFoundFromMe(true);
        return null;
      }
    }
    if (res.status === 409) {
      // This host uses a label the org has since retired, and the API names the
      // host it answers on now.
      const body = await res.json().catch(() => ({}));
      if (body?.code === 'ORGANIZATION_HOST_MOVED' && typeof body?.appSubdomain === 'string') {
        if (redirectToMovedHost(body.appSubdomain)) {
          return null;
        }
        // Same dead end as the pre-auth path, so the same screen. Without this
        // the two halves of one answer diverge on the identical condition: a
        // signed-out visitor gets "Workspace Not Found" while a signed-in one
        // falls through to the generic "Failed to load user" below.
        setMovedRedirectFailed(true);
        return null;
      }
    }
    if (res.status === 403) {
      const body = await res.json().catch(() => ({}));
      if (body?.error === 'Account deactivated') {
        setIsAccountDeactivated(true);
        return null;
      }
    }
    if (!res.ok) {
      throw new Error('Failed to load user');
    }
    const json = await res.json();
    const user = json.user as User;
    // Normalize null → "" so consumers can keep typing orgId as string; see User type.
    if (user.orgId === null || user.orgId === undefined) {
      user.orgId = '';
    }
    if (json.ephemeralEmailDomain) {
      user.ephemeralEmailDomain = json.ephemeralEmailDomain as string;
    }
    if (json.organizations) {
      user.organizations = json.organizations as Organization[];
    }
    return user;
  }, []);

  // Fetch current user (includes organizations in response)
  const {
    data: user,
    isLoading: meLoading,
    refetch: refetchMe,
  } = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    staleTime: 30_000,
  });

  // Organizations are now included in the /me response
  const organizations = user?.organizations;

  // The org this tab rendered, fixed at first load; the fetch interceptor sends it as an
  // expectation header. A later /me that reports a different org means another tab (or a
  // missed broadcast) moved the session, so reload instead of adopting it: adopting would
  // let this stale page write into the new org.
  React.useEffect(() => {
    if (!user?.orgId) return;
    const rendered = getRenderedOrganizationId();
    if (rendered === null) setRenderedOrganizationId(user.orgId);
    else if (rendered.toLowerCase() !== user.orgId.toLowerCase()) reloadForOrganizationChange();
  }, [user?.orgId]);

  // After an org-change reload, tell the user why the page just refreshed.
  const { toast } = useToast();
  React.useEffect(() => {
    if (consumeOrganizationChangedNotice()) toast.info('You switched organizations in another tab');
  }, [toast]);

  // Another tab switched the session's org: this tab is stale, reload into the new one.
  React.useEffect(() => {
    const channel = new BroadcastChannel(ORG_CHANNEL_NAME);
    channel.onmessage = (event: MessageEvent) => {
      if (event.data?.type === 'org-switched') reloadForOrganizationChange();
    };
    return () => channel.close();
  }, []);

  // Deep link: `?cofactor_org_id=<id>` (opened by the org switcher when leaving
  // a tenant host) asks to land on another org. The active org is the session
  // cookie's org, so once `/me` has loaded we ask the API to switch the cookie
  // and bounce back to this URL without the param. If the viewer is signed out
  // the param simply stays in the URL until they are authenticated.
  React.useEffect(() => {
    if (!user) return;
    const decision = resolveOrgDeepLink(window.location.href, user);
    if (decision.action === 'none') return;
    if (decision.action === 'strip') {
      window.history.replaceState(null, '', decision.cleanUrl);
      return;
    }
    window.location.replace(
      `${API_URL}/organizations/${decision.orgId}/switch?next=${encodeURIComponent(decision.cleanUrl)}`
    );
  }, [user]);

  // Switch organization mutation
  const switchOrgMutation = useMutation({
    mutationKey: ['switch-organization'],
    mutationFn: async ({ orgId }: { orgId: string; redirectUrl?: string }) => {
      return switchOrganizationRequest({ orgId });
    },
    onSuccess: (_data, variables) => {
      const channel = new BroadcastChannel(ORG_CHANNEL_NAME);
      channel.postMessage({ type: 'org-switched' });
      channel.close();
      window.location.href = variables.redirectUrl ?? '/';
    },
  });

  // Create organization mutation
  const createOrgMutation = useMutation({
    mutationFn: async ({ name, first }: CreateOrgInput) => {
      const res = await fetch(API_URL + '/organizations', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name, first }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to create organization');
      return res.json();
    },
    onSuccess: (data: { ok: boolean; id: string }, variables) => {
      // Switch to the newly created organization
      switchOrgMutation.mutate({ orgId: data.id, redirectUrl: variables.redirectUrl });
    },
  });

  // Set default organization mutation
  const setDefaultOrgMutation = useMutation({
    mutationKey: ['set-default-organization'],
    mutationFn: async (orgId: string) => {
      return setDefaultOrganizationRequest({ orgId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });

  // Sign out function
  const signOut = React.useCallback(() => {
    window
      .fetch(API_URL + '/auth/signout', {
        method: 'POST',
        credentials: 'include',
      })
      .then(() => {
        window.location.href = '/';
      });
  }, []);

  // Refetch user wrapper
  const refetchUser = React.useCallback(async () => {
    await refetchMe();
  }, [refetchMe]);

  const value: AuthContextType = {
    user: user ?? null,
    organizations,
    // Both queries, not just `/me`. `isWorkspaceNotFound` and the branded login
    // name are answers only `/auth/config` has, so a consumer that rendered as
    // soon as `/me` settled would show the ordinary sign-in form and correct
    // itself a moment later — on an unclaimed host, exactly the screen this
    // feature removes, appearing as an intermittent flash.
    //
    // `hostPending` is the config query's `isPending`, not its `isLoading`, and
    // the difference is deliberate: a query started while the browser is
    // offline stays pending with `fetchStatus: 'paused'` (v5's `isLoading` is
    // `isPending && isFetching`, so it would be false there). This therefore
    // parks on the loading screen until connectivity returns instead of
    // falling through — the trade for never painting a sign-in form we would
    // have to retract. Do not "fix" it to `isLoading`; that reintroduces the
    // flash.
    isLoading: meLoading || hostPending,
    isAccountDeactivated,
    isWorkspaceNotFound,
    hostOrganizationName: loginHost?.kind === 'organization' ? loginHost.name : null,
    switchOrg: (orgId: string, redirectUrl?: string) =>
      switchOrgMutation.mutate({ orgId, redirectUrl }),
    createOrg: (input: CreateOrgInput) => createOrgMutation.mutate(input),
    setDefaultOrg: (orgId: string) => setDefaultOrgMutation.mutate(orgId),
    signOut,
    refetchUser,
    isSwitchingOrg: switchOrgMutation.isPending,
    isCreatingOrg: createOrgMutation.isPending,
    isSettingDefaultOrg: setDefaultOrgMutation.isPending,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
